/**
 * E15 限时任务管理 - 创建/编辑抽屉
 */
import React, { useEffect } from 'react';
import { Drawer, Form, Input, Select, InputNumber, DatePicker, Button, Space, message } from 'antd';
import dayjs from 'dayjs';
import type { LimitedTask } from './types';
import { createLimitedTask, updateLimitedTask } from './api';

const { RangePicker } = DatePicker;

/** 奖励类型选项 */
const REWARD_TYPE_OPTIONS = [
  { label: '固定金币', value: 'fixed' },
  { label: '百分比加成', value: 'percent' },
];

/** 奖励币种选项 */
const COIN_TYPE_OPTIONS = [
  { label: '活动金币', value: 'activity' },
  { label: '充值金币', value: 'purchased' },
  { label: '会员金币', value: 'member' },
];

/**
 * 触发条件类型选项（限时任务支持的事件）
 *
 * 当前启用：
 * - first_topup / first_subscription：shopping-mall UserAssertServiceImpl 在 Google/Apple
 *   充值/订阅成功后自动 Kafka 触发；客户端卡片点击分别跳充值页 / 订阅页
 * - publish_plaza：artwork-content ArtworkServiceImpl.publishV2 发布作品到广场时发 Kafka
 *   (E15 版本 >2.5.1)；shopping-mall TaskProgressConsumer 消费 → updateTaskProgress
 *   + recordDailyPublish；Android TaskJumpUtils.PUBLISH_PLAZA 处理跳转
 *
 * 历史下线（仍保留在 DB 中的旧任务可读但不能新建）：
 * - topup_any / custom：后端零代码处理，配置后永远不发奖
 * - create_with_style：与后端真实事件名 style_used_count 不匹配
 * - share_social：当前业务方向不再用限时任务承载
 */
const CONDITION_TYPE_OPTIONS = [
  { label: '首次充值', value: 'first_topup' },
  { label: '首次订阅', value: 'first_subscription' },
  { label: '发布作品到广场', value: 'publish_plaza' },
];

interface Props {
  open: boolean;
  editingTask?: LimitedTask;
  onClose: () => void;
  onSuccess: () => void;
}

const LimitedTaskDrawer: React.FC<Props> = ({ open, editingTask, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const isEdit = !!editingTask?.id;

  useEffect(() => {
    if (open && editingTask) {
      form.setFieldsValue({
        ...editingTask,
        timeRange: editingTask.startTime && editingTask.endTime
          ? [dayjs(editingTask.startTime), dayjs(editingTask.endTime)]
          : undefined,
      });
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({ rewardType: 'fixed', rewardCoinType: 'activity', status: 1, conditionValue: 1, sortOrder: 0 });
    }
  }, [open, editingTask]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const [startTime, endTime] = values.timeRange || [];
      const payload: LimitedTask = {
        ...values,
        id: editingTask?.id,
        startTime: startTime?.format('YYYY-MM-DD HH:mm:ss'),
        endTime: endTime?.format('YYYY-MM-DD HH:mm:ss'),
      };
      delete (payload as any).timeRange;

      if (isEdit) {
        await updateLimitedTask(payload);
        message.success('更新成功');
      } else {
        await createLimitedTask(payload);
        message.success('创建成功');
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('提交失败', error);
    }
  };

  return (
    <Drawer
      title={isEdit ? '编辑限时任务' : '创建限时任务'}
      open={open}
      onClose={onClose}
      width={600}
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={handleSubmit}>保存</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="code" label="任务编码" rules={[{ required: true, message: '请输入任务编码' }]}>
          <Input placeholder="如: sakura_topup_2026" disabled={isEdit} />
        </Form.Item>

        <Form.Item name="nameEn" label="英文名称" rules={[{ required: true }]}>
          <Input placeholder="Sakura Festival Top-Up" />
        </Form.Item>
        <Form.Item name="nameJa" label="日文名称" rules={[{ required: true }]}>
          <Input placeholder="桜祭り限定チャージ" />
        </Form.Item>

        <Form.Item name="descEn" label="英文描述">
          <Input.TextArea placeholder="Top up any amount for extra 20% activity coins" rows={2} />
        </Form.Item>
        <Form.Item name="descJa" label="日文描述">
          <Input.TextArea placeholder="チャージすると20%のイベントコインをプレゼント" rows={2} />
        </Form.Item>

        <Form.Item name="rewardType" label="奖励类型" rules={[{ required: true }]}>
          <Select options={REWARD_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item name="rewardValue" label="奖励值" rules={[{ required: true }]}
          extra="固定金币填数字(如20)，百分比填百分数(如20%)">
          <Input placeholder="20 或 20%" />
        </Form.Item>
        <Form.Item name="rewardCoinType" label="奖励币种">
          <Select options={COIN_TYPE_OPTIONS} />
        </Form.Item>

        <Form.Item name="conditionType" label="触发条件" rules={[{ required: true }]}>
          <Select options={CONDITION_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item name="conditionValue" label="目标值">
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="styleId" label="关联风格ID" extra="不填表示不限风格，填写后仅使用该风格创作的作品才算完成">
          <InputNumber min={1} style={{ width: '100%' }} placeholder="如: 78 (NightSky)" />
        </Form.Item>
        <Form.Item name="drawType" label="生图类型" extra="不选表示不限类型">
          <Select allowClear placeholder="不限" options={[
            { label: '文生图', value: 0 },
            { label: '图生图', value: 1 },
          ]} />
        </Form.Item>

        <Form.Item name="timeRange" label="活动时间" rules={[{ required: true, message: '请选择活动时间' }]}>
          <RangePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="icon" label="图标URL">
          <Input placeholder="https://..." />
        </Form.Item>
        <Form.Item name="color" label="主题色">
          <Input placeholder="#E84393" />
        </Form.Item>
        <Form.Item name="sortOrder" label="排序">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default LimitedTaskDrawer;
